import services from 'services';
import importMaps from 'services/importMaps';
import {describeThrow, describeError} from 'utils/errors';
import {apiUrl} from 'constants/app';
import {
	maxZoom,
	buildingsMinZoom,
	styles as mapStyles,
	buildingsYearMinZoom,
	groundwaterAreasMinZoom,
} from 'constants/maps';
import {
	safeGetFeaturesAtPixel,
	safeHasFeatureAtPixel,
	addTooltipToLayers,
	renderBuildingTooltip,
	createMarkerLayer,
	_getMapTileSource,
} from 'io/maps';
import {deleteChildren} from 'utils/dom';
import {encodeQuery} from 'utils/url';
import {getJsonProperty, getJsonProperties, concreteBuildingStyle} from 'utils/maps';
import {getResponseData} from 'utils/app';
import * as normalize from 'utils/normalize';
import {mapTooltip} from 'styles/fragments';

let intl = null;
services.waitFor('intl').then(x => (intl = x));

let httpJson = null;
services.waitFor('api').then(x => (httpJson = x.httpJson));

const decorateMapMethod = method => args =>
	importMaps()
		.then(method(args))
		.catch(describeThrow(intl.formatMessage({id: 'Error loading map'})));

// resources

export const postBuilding = building =>
	httpJson('post', '/buildings', {}, {body: building})
		.catch(e => {
			const conflict = !!e.response && e.response.status === 409;
			return conflict
				? e.response.json().then(body => describeThrow(body.message, e))
				: Promise.reject(
						describeError(intl.formatMessage({id: 'Failed to create building'}), e),
				  );
		})
		.then(getResponseData(normalize.building));

// TODO: localAttrs isn't in use anywhere yet
const _getBuildingsStyle =
	({zoom, filters, selectionId, manufacturingLimit, localAttrs = {}}) =>
	imports => {
		const {openLayers: ol} = imports;

		const sty = mapStyles(ol);
		const keys = ['id', 'encounterState', 'manufacturingYear', 'encounterDate', 'banned'];

		return feature => {
			const {id, encounterState, manufacturingYear, encounterDate, banned} =
				getJsonProperties(feature, keys);

			// const local = localAttrs[getJsonProperty(feature, 'id')];
			// const encounterState = local
			// 	? local.encounterState
			// 	: getJsonProperty(feature, 'encounterState');

			if (filters) {
				if (
					filters.encounterState &&
					filters.encounterState !== 'any' &&
					encounterState !== filters.encounterState
				)
					return null;
				if (
					filters.minYear &&
					(manufacturingYear < filters.minYear || !manufacturingYear)
				)
					return null;
				if (
					filters.maxYear &&
					(manufacturingYear > filters.maxYear || !manufacturingYear)
				)
					return null;
			}

			const selected = selectionId === id;
			const building = {encounterState, encounterDate, banned, manufacturingYear};

			return new ol.style.Style({
				...concreteBuildingStyle(ol, {
					geomType: feature.getGeometry().getType(),
					zoom,
					style: sty.buildingStyleProps({
						building,
						manufacturingLimit,
						selected,
						preferD2dState: true,
					}),
				}),
				zIndex: selected ? 1 : 0,
				text:
					zoom >= buildingsYearMinZoom
						? new ol.style.Text({
								text: manufacturingYear ? `${manufacturingYear}` : '',
								overflow: true,
						  })
						: null,
			});
		};
	};
export const getBuildingsStyle = decorateMapMethod(_getBuildingsStyle);

const _getBuildingsSource =
	({apiToken, organizationId}) =>
	imports => {
		const {openLayers: ol} = imports;

		return new ol.source.VectorTile({
			format: new ol.format.MVT({dataProjection: 'EPSG:4326'}),
			// prettier-ignore
			url: `${apiUrl}/maps/buildings/mvt/{z}/{x}/{y}${encodeQuery({organizationId, token: apiToken})}`,
			maxZoom: maxZoom,
		});
	};
export const getBuildingsSource = decorateMapMethod(_getBuildingsSource);

const createBuildingsLayer =
	({
		apiToken,
		organizationId,
		initialZoom,
		initialFilters,
		selectionId,
		manufacturingLimit,
	}) =>
	imports => {
		const {openLayers: ol} = imports;

		const layer = new ol.layer.VectorTile({
			source: _getBuildingsSource({apiToken, organizationId})(imports),
			style: _getBuildingsStyle({
				zoom: initialZoom,
				filters: initialFilters,
				selectionId,
				manufacturingLimit,
			})(imports),
			visible: initialZoom >= buildingsMinZoom,
		});

		return layer;
	};

const createLocationFeatures = imports => {
	const {openLayers: ol} = imports;

	const position = new ol.Feature();
	const accuracy = new ol.Feature();

	position.setStyle(
		new ol.style.Style({
			image: new ol.style.Circle({
				radius: 6,
				fill: new ol.style.Fill({
					color: '#3399CC',
				}),
				stroke: new ol.style.Stroke({
					color: '#fff',
					width: 2,
				}),
			}),
		}),
	);

	return {position, accuracy};
};

const createLocationLayer =
	(features = []) =>
	imports => {
		const {openLayers: ol} = imports;

		const locationSource = new ol.source.VectorSource({
			features,
		});

		const locationLayer = new ol.layer.VectorLayer({
			source: locationSource,
			visible: false,
		});

		return locationLayer;
	};

// groundwaterAreas layer

const _getGroundwaterAreasStyle = () => imports => {
	const {openLayers: ol} = imports;

	const sty = mapStyles(ol);

	return feature => {
		return new ol.style.Style({
			fill: sty.groundwaterAreasFill,
			stroke: sty.groundwaterAreasStroke,
		});
	};
};
export const getGroundwaterAreasStyle = decorateMapMethod(_getGroundwaterAreasStyle);

const _getGroundwaterAreasSource =
	({apiToken}) =>
	imports => {
		const {openLayers: ol} = imports;

		return new ol.source.VectorTile({
			format: new ol.format.MVT({dataProjection: 'EPSG:4326'}),
			url: `${apiUrl}/maps/groundwaterAreas/mvt/{z}/{x}/{y}${encodeQuery({
				token: apiToken,
			})}`,
			maxZoom: maxZoom,
		});
	};
export const getGroundwaterAreasSource = decorateMapMethod(_getGroundwaterAreasSource);

const createGroundwaterAreasLayer =
	({apiToken, initialZoom, visible}) =>
	imports => {
		const {openLayers: ol} = imports;

		const layer = new ol.layer.VectorTile({
			source: _getGroundwaterAreasSource({apiToken})(imports),
			style: _getGroundwaterAreasStyle()(imports),
			visible: visible ? initialZoom >= groundwaterAreasMinZoom : false,
		});

		return layer;
	};

const _initFreerideMap =
	({
		apiToken,
		organizationId,
		initialZoom,
		initialCenter,
		initialFilters,
		selectionId,
		onBuildingClick,
		onMapLocationChanged,
		onGeolocationChanged,
		onGeolocationError,
		setFollowingGeolocation,
		getFollowingGeolocation,
		manufacturingLimit,
		mapSourceProps,
		layersVisibility,
		getLayersVisibility,
	}) =>
	imports => {
		// keep some local state
		let currHoverBuildingId = null;

		const {openLayers: ol} = imports;

		const mapEl = document.querySelector('#freeride-map');
		if (!mapEl) {
			return Promise.reject(new Error('Map element not found'));
		}

		const view = new ol.View({
			projection: 'EPSG:3857',
			center: initialCenter,
			zoom: initialZoom,
			maxZoom,
			enableRotation: false,
			constrainResolution: true,
		});

		const buildingsLayer = createBuildingsLayer({
			apiToken,
			organizationId,
			initialZoom,
			initialFilters,
			selectionId,
			manufacturingLimit,
		})(imports);

		const groundwaterAreasLayer = createGroundwaterAreasLayer({
			apiToken,
			initialZoom,
			visible: layersVisibility.groundwaterAreasLayer,
		})(imports);

		const mapSource = _getMapTileSource(mapSourceProps)(imports);
		const mapLayer = new ol.layer.Tile({source: mapSource});

		const markerLayer = createMarkerLayer(imports);

		// geolocation

		const locationFeatures = createLocationFeatures(imports);
		const locationLayer = createLocationLayer(Object.values(locationFeatures))(imports);

		const geolocation = new ol.Geolocation({
			trackingOptions: {
				enableHighAccuracy: true,
			},
			projection: view.getProjection(),
		});

		geolocation.on('change:position', () => {
			const p = geolocation.getPosition();
			if (p) {
				onGeolocationChanged(p);
				locationFeatures.position.setGeometry(new ol.geom.Point(p));

				// focus view on the new geolocation if enabled and no animation / user interaction in progress
				const animating = view.getAnimating();
				const interacting = view.getInteracting();
				if (getFollowingGeolocation() && !animating && !interacting) {
					view.animate({center: p, zoom: maxZoom});
				}
			}
		});

		geolocation.on('change:accuracyGeometry', () => {
			locationFeatures.accuracy.setGeometry(geolocation.getAccuracyGeometry());
		});

		geolocation.on('error', error => {
			onGeolocationError(error);
		});

		// map

		const map = new ol.Map({
			target: 'freeride-map',
			view,
			layers: [
				mapLayer,
				groundwaterAreasLayer,
				buildingsLayer,
				locationLayer,
				markerLayer,
			],
		});

		map.on('movestart', e => {
			if (getFollowingGeolocation()) {
				// stop map from following geolocation if user interacts with map
				const interacting = view.getInteracting();
				if (interacting) setFollowingGeolocation(false);
			}
		});

		map.on('moveend', e => {
			const zoom = map.getView().getZoom();
			const center = map.getView().getCenter();

			onMapLocationChanged({z: zoom, x: center[0], y: center[1]});

			// toggle buildings layer
			if (zoom >= buildingsMinZoom) {
				buildingsLayer.setVisible(true);
			} else {
				buildingsLayer.setVisible(false);
			}

			// toggle groundwater areas layer
			if (
				zoom >= groundwaterAreasMinZoom &&
				getLayersVisibility().groundwaterAreasLayer
			) {
				groundwaterAreasLayer.setVisible(true);
			} else {
				groundwaterAreasLayer.setVisible(false);
			}

			// stop map from following geolocation if zoom lowered
			if (getFollowingGeolocation() && zoom < maxZoom) {
				setFollowingGeolocation(false);
			}
		});

		map.on('click', e => {
			const features = safeGetFeaturesAtPixel(map, e.pixel, {
				layerFilter: l => l === buildingsLayer,
			});
			if (!features) return;
			const buildingId = getJsonProperty(features[0], 'id');
			onBuildingClick(buildingId, e.coordinate);
		});

		map.on('pointermove', e => {
			if (e.dragging) return;
			const pixel = map.getEventPixel(e.originalEvent);
			const hit = safeHasFeatureAtPixel(map, pixel, {
				layerFilter: l => l === buildingsLayer || l === markerLayer,
			});
			map.getViewport().style.cursor = hit ? 'pointer' : '';
		});

		const buildingTooltipEl = document.createElement('div');
		buildingTooltipEl.id = 'freeride-building-tooltip';
		buildingTooltipEl.style.cssText = mapTooltip;

		const buildingTooltip = new ol.Overlay({
			element: buildingTooltipEl,
			positioning: 'bottom-center',
			offset: [0, -5],
		});

		const updateBuildingTooltip = buildingFeature => {
			const {
				id,
				address,
				manufacturingYear,
				encounterState,
				encounterDate,
				clientId,
				clientFirstName,
				clientLastName,
			} = getJsonProperties(buildingFeature, [
				'id',
				'address',
				'manufacturingYear',
				'encounterState',
				'encounterDate',
				'clientId',
				'clientFirstName',
				'clientLastName',
			]);

			if (id === currHoverBuildingId) return;

			currHoverBuildingId = id;
			deleteChildren(buildingTooltipEl);
			renderBuildingTooltip({
				tooltipEl: buildingTooltipEl,
				address,
				manufacturingYear,
				encounterState,
				encounterDate,
				clientId,
				clientFirstName,
				clientLastName,
			});
		};

		addTooltipToLayers({
			mapEl,
			map,
			layers: [buildingsLayer],
			tooltipEl: buildingTooltipEl,
			tooltip: buildingTooltip,
			updateTooltip: updateBuildingTooltip,
		});

		return Promise.resolve({
			map,
			buildingsLayer,
			locationLayer,
			geolocation,
			mapLayer,
			markerLayer,
			groundwaterAreasLayer,
		});
	};
export const initFreerideMap = decorateMapMethod(_initFreerideMap);
